import clsx from 'clsx';
import type { FormEvent, FunctionComponent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { normalizeProfileTaskTitle } from '../validation/profile-task-validation';

// innerText, not textContent: pasted or inserted line breaks become <br>/block elements, which
// textContent silently drops — concatenating the lines without separators.
const getEditableValue = (element: HTMLElement): string => normalizeProfileTaskTitle(element.innerText ?? '').trim();

// The tile focus scope moves focus to its tabindex="-1" root section after mount; such
// programmatic sinks (and <body>) are fair to reclaim from — real interactive elements are not.
const isReclaimableFocusTarget = (element: HTMLElement, active: Element | null): boolean =>
    active === null ||
    active === document.body ||
    (active instanceof HTMLElement && active.tabIndex === -1 && active.contains(element));

// Take focus from wherever it is (typically the "Add task" button) until it sticks once; after
// that, only reclaim it from programmatic sinks so a deliberate focus move is never fought.
const claimFocus = (element: HTMLElement, hadFocus: boolean): 'claimed' | 'held' | 'retry' | 'stolen' => {
    const active = document.activeElement;
    if (active === element) {
        if (hadFocus) {
            return 'held';
        }
        placeCaretAtEnd(element);
        return 'claimed';
    }
    if (hadFocus && !isReclaimableFocusTarget(element, active)) {
        return 'stolen';
    }
    element.focus();
    if (document.activeElement !== element) {
        return 'retry';
    }
    if (hadFocus) {
        return 'held';
    }
    placeCaretAtEnd(element);
    return 'claimed';
};

const placeCaretAtEnd = (element: HTMLElement): void => {
    const selection = window.getSelection();
    if (selection === null || element.childNodes.length === 0) {
        return;
    }
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
};

export const TaskTitleEditable: FunctionComponent<{
    readonly autoFocus?: boolean;
    readonly onChange: (value: string) => void;
    readonly placeholder: string;
    readonly value: string;
}> = ({ autoFocus = false, onChange, placeholder, value }) => {
    const latestValueRef = useRef(value);
    const elementRef = useRef<HTMLSpanElement | null>(null);
    latestValueRef.current = value;

    const setElement = useCallback((element: HTMLSpanElement | null): void => {
        elementRef.current = element;
        if (element !== null && element.textContent !== latestValueRef.current) {
            element.textContent = latestValueRef.current;
        }
    }, []);

    useEffect(() => {
        if (!autoFocus) {
            return undefined;
        }

        // The tile mounts mid enter-animation: an early focus() call is dropped or reset when the
        // animation settles, so keep claiming on animation frames until the window elapses.
        let cancelled = false;
        let hadFocus = false;
        const startedAt = performance.now();
        const tick = (): void => {
            if (cancelled) {
                return;
            }
            const element = elementRef.current;
            const outcome = element === null ? 'retry' : claimFocus(element, hadFocus);
            if (outcome === 'stolen') {
                return;
            }
            hadFocus = hadFocus || outcome === 'claimed';
            if (performance.now() - startedAt < 2000) {
                requestAnimationFrame(tick);
            }
        };
        tick();

        return () => {
            cancelled = true;
        };
    }, [autoFocus]);

    useLayoutEffect(() => {
        const element = elementRef.current;
        if (element === null || document.activeElement === element) {
            return;
        }
        if (element.textContent !== value) {
            element.textContent = value;
        }
    }, [value]);

    const handleInput = (event: FormEvent<HTMLSpanElement>): void => {
        const element = event.currentTarget;
        const nextValue = getEditableValue(element);
        if (nextValue === '' && element.childNodes.length > 0) {
            element.textContent = '';
        } else if (element.childElementCount > 0) {
            // The title is single-line: flatten inserted <br>/block markup back to one text node.
            element.textContent = nextValue;
            placeCaretAtEnd(element);
        }
        onChange(nextValue);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    return (
        // biome-ignore lint/a11y/useSemanticElements: the title editor intentionally uses contenteditable so long titles wrap while editing, mirroring the job profile title.
        <span
            ref={setElement}
            aria-label={placeholder}
            aria-multiline={false}
            aria-placeholder={placeholder}
            className={clsx(
                'm-0 block min-h-[1lh] w-full cursor-text whitespace-normal outline-none empty:block',
                'empty:before:pointer-events-none empty:before:text-text-placeholder empty:before:content-[attr(data-placeholder)]',
                'text-left text-[1.375rem] font-medium leading-normal tracking-normal text-text-primary',
                '[overflow-wrap:anywhere]',
            )}
            contentEditable={true}
            data-placeholder={placeholder}
            data-testid="profile-task-title-input"
            role="textbox"
            spellCheck={true}
            suppressContentEditableWarning={true}
            tabIndex={0}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
        />
    );
};
